import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TourGuide = ({ projects, isActive, onTourComplete, onProjectFocus }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  const tourSteps = [
    {
      title: "Welcome to the Portfolio Gallery",
      description: "Experience projects in immersive 3D environments. Each project tells a unique story through spatial design.",
      project: null,
      duration: 3000
    },
    {
      title: "Web Development Showcase",
      description: "Explore interactive web applications with live preview capabilities and detailed case studies.",
      project: projects?.find(p => p?.category === 'Web Development'),
      duration: 4000
    },
    {
      title: "Creative Coding Experiments",
      description: "Discover innovative coding projects that push the boundaries of digital creativity.",
      project: projects?.find(p => p?.category === 'Creative Coding'),
      duration: 4000
    },
    {
      title: "Brand Campaign Successes",
      description: "See how strategic design thinking creates memorable brand experiences.",
      project: projects?.find(p => p?.category === 'Brand Campaigns'),
      duration: 4000
    },
    {
      title: "Explore Freely",
      description: "Tour complete! Navigate freely through the gallery or start a new guided experience.",
      project: null,
      duration: 2000
    }
  ];

  useEffect(() => {
    let timer;
    if (isActive && isPlaying && currentStep < tourSteps?.length - 1) {
      timer = setTimeout(() => {
        nextStep();
      }, tourSteps?.[currentStep]?.duration);
    }
    return () => clearTimeout(timer);
  }, [currentStep, isActive, isPlaying]);

  useEffect(() => {
    if (isActive && tourSteps?.[currentStep]?.project) {
      onProjectFocus(tourSteps?.[currentStep]?.project);
    }
  }, [currentStep, isActive]);

  const nextStep = () => {
    if (currentStep < tourSteps?.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      completeTour();
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const startTour = () => {
    setCurrentStep(0);
    setIsPlaying(true);
  };

  const pauseTour = () => {
    setIsPlaying(false);
  };

  const completeTour = () => {
    setIsPlaying(false);
    setCurrentStep(0);
    onTourComplete();
  };

  if (!isActive) {
    return (
      <motion.div
        className="fixed bottom-6 right-6 z-40"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.8 }}
      >
        <Button
          variant="default"
          onClick={startTour}
          iconName="Play"
          iconPosition="left"
          className="bg-accent text-background hover:bg-accent/90 cosmic-glow shadow-cosmic-lg"
        >
          Start Guided Tour
        </Button>
      </motion.div>
    );
  }

  const currentTourStep = tourSteps?.[currentStep];

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 pointer-events-none"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        {/* Tour Overlay */}
        <div className="absolute inset-0 bg-background/20 backdrop-blur-sm" />

        {/* Tour Panel */}
        <motion.div
          className="absolute bottom-6 left-1/2 transform -translate-x-1/2 w-full max-w-2xl mx-4 pointer-events-auto"
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
        >
          <div className="glass-panel rounded-2xl p-6 shadow-cosmic-lg border border-accent/20">
            {/* Progress Bar */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-body text-muted-foreground">
                  Step {currentStep + 1} of {tourSteps?.length}
                </span>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="ghost"
                    size="xs"
                    onClick={pauseTour}
                    iconName={isPlaying ? "Pause" : "Play"}
                    iconSize={14}
                    className="text-muted-foreground hover:text-foreground"
                  />
                  <Button
                    variant="ghost"
                    size="xs"
                    onClick={completeTour}
                    iconName="X"
                    iconSize={14}
                    className="text-muted-foreground hover:text-foreground"
                  />
                </div>
              </div>
              <div className="w-full bg-surface/50 rounded-full h-2">
                <motion.div
                  className="bg-accent rounded-full h-2"
                  initial={{ width: 0 }}
                  animate={{ width: `${((currentStep + 1) / tourSteps?.length) * 100}%` }}
                  transition={{ duration: 0.5 }}
                />
              </div>
            </div>

            {/* Content */}
            <div className="space-y-4">
              <div>
                <h3 className="font-headline font-bold text-xl text-foreground mb-2">
                  {currentTourStep?.title}
                </h3>
                <p className="font-body text-muted-foreground leading-relaxed">
                  {currentTourStep?.description}
                </p>
              </div>

              {/* Project Info */}
              {currentTourStep?.project && (
                <div className="glass-panel-light rounded-lg p-4">
                  <div className="flex items-center space-x-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      currentTourStep?.project?.category === 'Web Development' ?'bg-accent/20 text-accent'
                        : currentTourStep?.project?.category === 'Creative Coding' ?'bg-cosmic-energy/20 text-cosmic-energy' :'bg-cosmic-cta/20 text-cosmic-cta'
                    }`}>
                      <Icon 
                        name={currentTourStep?.project?.category === 'Web Development' ? 'Monitor' : 
                              currentTourStep?.project?.category === 'Creative Coding' ? 'Zap' : 'Megaphone'} 
                        size={20} 
                      />
                    </div>
                    <div>
                      <h4 className="font-body font-medium text-foreground">
                        {currentTourStep?.project?.title}
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        {currentTourStep?.project?.client} • {currentTourStep?.project?.year}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Controls */}
            <div className="flex items-center justify-between mt-6">
              <Button
                variant="outline"
                onClick={prevStep}
                disabled={currentStep === 0}
                iconName="ChevronLeft"
                iconPosition="left"
                className="border-border/30 text-muted-foreground hover:text-foreground disabled:opacity-50"
              >
                Previous
              </Button>

              <div className="flex items-center space-x-2">
                {tourSteps?.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentStep(idx)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      idx === currentStep
                        ? 'bg-accent scale-125'
                        : idx < currentStep
                        ? 'bg-accent/50' :'bg-surface/50'
                    }`}
                  />
                ))}
              </div>

              <Button
                variant="default"
                onClick={currentStep === tourSteps?.length - 1 ? completeTour : nextStep}
                iconName={currentStep === tourSteps?.length - 1 ? "Check" : "ChevronRight"}
                iconPosition="right"
                className="bg-accent text-background hover:bg-accent/90"
              >
                {currentStep === tourSteps?.length - 1 ? 'Complete' : 'Next'}
              </Button>
            </div>

            {/* Auto-play Timer */}
            {isPlaying && currentStep < tourSteps?.length - 1 && (
              <motion.div
                className="absolute bottom-0 left-0 right-0 h-1 bg-accent/30 rounded-b-2xl overflow-hidden"
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: currentTourStep?.duration / 1000, ease: "linear" }}
                style={{ transformOrigin: "left" }}
              >
                <div className="w-full h-full bg-accent" />
              </motion.div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default TourGuide;